import { useRef, useState } from "react";
import { useLocation } from "wouter";
import {
  chosenModelsFitOneRun,
  chosenModelsSelect,
  type DbtJobView,
  type RunDbtJobsBody,
  type StartTransformRunResponse,
  type TransformationGraph,
} from "@shared/types/transformations";
import { useInlineError } from "@/lib/feedback/InlineErrorBar";
import { useNotice } from "@/lib/feedback/NoticeProvider";
import { useDbtJobRuns, useStartTransformRun } from "@/hooks/use-dbt-jobs";
import { transformQueueOf } from "@/lib/transformations/transformQueue";
import { newIdempotencyKey, referenceIdOf, transformNotReadyDetails } from "@/lib/api";
import { modelsWithParents, willRunWords } from "@/lib/transformations/willRun";
import { absolutePath } from "@/lib/routes";
import {
  isRunnable,
  notReadyChecklist,
  orderByDependencies,
  type NotReadyItem,
} from "./transformFormatting";

/**
 * The Run dbt jobs dialog's state, in the order the dialog reads it: which dbt jobs the run is for,
 * which models were chosen, and starting the run. Split from `RunDbtJobsDialog.tsx` (#1411) so the
 * component only lays out what these answer.
 */

/** #1293: every job upstream of this one (direct or not), which its run leaves out. Cycle-safe. */
function upstreamOf(job: DbtJobView, all: DbtJobView[]): DbtJobView[] {
  const byId = new Map(all.map((row) => [row.id, row]));
  const seen = new Set<string>([job.id]);
  const found: DbtJobView[] = [];
  const queue = [...job.dependsOn];
  while (queue.length > 0) {
    const id = queue.shift() as string;
    if (seen.has(id)) continue;
    seen.add(id);
    const row = byId.get(id);
    if (!row) continue;
    found.push(row);
    queue.push(...row.dependsOn);
  }
  return found;
}

/** The dbt jobs a run is for: all enabled ones, a selection of them (taken once per open), or one. */
export function useRunScope(
  transformations: DbtJobView[],
  onlyJobId: string | undefined,
  initialSelection: readonly string[] | undefined,
) {
  // The selection is taken once per open, like the idempotency key: the run the dialog describes
  // is the run the button starts.
  const [selection] = useState(() => {
    if (onlyJobId) return new Set([onlyJobId]);
    return initialSelection ? new Set(initialSelection) : null;
  });
  // #1293: the one job of a Run now, and the jobs it depends on, which the run leaves out.
  const oneJobAsked = onlyJobId !== undefined;
  const onlyJob = onlyJobId ? transformations.find((row) => row.id === onlyJobId) : undefined;
  const dependencies = onlyJob ? upstreamOf(onlyJob, transformations) : [];
  const enabled = transformations.filter((row) => row.enabled);
  const runnable = orderByDependencies(
    transformations.filter((row) => isRunnable(row) && (!selection || selection.has(row.id))),
  );
  const notSelected = selection
    ? orderByDependencies(
        transformations.filter((row) => isRunnable(row) && !selection.has(row.id)),
      )
    : [];
  const disabled = transformations.filter((row) => !row.enabled);
  const withoutSelector = enabled.filter((row) => !isRunnable(row));
  const mergedSelector = runnable.map((row) => row.dbtSelector ?? "").join(" ");
  const withTests = runnable.filter((row) => row.runTests);
  // What the server pins: the runnable ones named when a selection was passed, else every
  // enabled dbt job (`POST /dbt-jobs/run` with no body).
  const requestIds = selection ? runnable.map((row) => row.id) : enabled.map((row) => row.id);
  return {
    hasSelection: selection !== null,
    oneJobAsked,
    onlyJob,
    dependencies,
    enabled,
    runnable,
    notSelected,
    disabled,
    withoutSelector,
    mergedSelector,
    withTests,
    requestIds,
  };
}

/** Round 6 (T3): the models chosen on the Models view (taken once per open) and the two options. */
export function useChosenModels(
  chosenModels: readonly string[] | undefined,
  graph: TransformationGraph | undefined,
) {
  const [chosen] = useState(() => (chosenModels ? [...chosenModels] : null));
  const [buildParents, setBuildParents] = useState(false);
  const [runTests, setRunTests] = useState(true);
  const chosenSelect = chosen ? chosenModelsSelect(chosen, buildParents) : "";
  const willRun = chosen
    ? willRunWords(chosen, buildParents, buildParents ? modelsWithParents(graph, chosen) : null)
    : "";
  // Past either limit the run body refuses (500 names, 100,000 characters): review r1.
  const chosenTooLong = chosen ? !chosenModelsFitOneRun(chosen, buildParents) : false;
  return {
    chosen,
    buildParents,
    setBuildParents,
    runTests,
    setRunTests,
    chosenSelect,
    willRun,
    chosenTooLong,
  };
}

/** The notice once the run is accepted: the chosen models it builds, or the dbt jobs it runs. */
function runStartedSentence(result: StartTransformRunResponse, chosen: string[] | null): string {
  if (chosen) {
    const count = result.models?.length ?? chosen.length;
    return `Run ${result.runId} is building ${count} chosen model${count === 1 ? "" : "s"}.`;
  }
  const jobs = result.dbtJobIds.length;
  return `Run ${result.runId} is running ${jobs} dbt job${jobs === 1 ? "" : "s"}.`;
}

/** What the new run waits behind, read from the queue: the running run if any, and runs also waiting. */
export interface QueuedBehind {
  holderRunId: string | null;
  waiting: number;
}

/**
 * #1419: the notice's title and sentence. A run that waits for another says it is queued and which
 * run it waits for, in the same "Run <id>" form the dialog's own bar uses. It reads "queued" only
 * when the queue read shows a run holding or waiting for the slot. Nothing is called "in progress"
 * unless it is running.
 */
function runNotice(
  result: StartTransformRunResponse,
  chosen: string[] | null,
  behind: QueuedBehind | null,
): { title: string; sentence: string } {
  if (!behind) return { title: "Run started", sentence: runStartedSentence(result, chosen) };
  const { holderRunId, waiting } = behind;
  let sentence = `Run ${result.runId} starts when the ${waiting === 1 ? "run ahead of it finishes" : "runs ahead of it finish"}.`;
  if (holderRunId) {
    sentence =
      waiting > 0
        ? `Run ${result.runId} waits for run ${holderRunId} and ${waiting} other waiting run${waiting === 1 ? "" : "s"}.`
        : `Run ${result.runId} starts when run ${holderRunId} finishes.`;
  }
  return { title: "Run queued", sentence };
}

/** The server's refusal: what is not ready, each with where to fix it, and the reference id. */
export interface RunRefusal {
  checklist: NotReadyItem[];
  message: string;
  reference: string | null;
}

/** What the run asks for: the chosen models, the selected dbt jobs, or every enabled one. */
function runBodyOf(
  chosen: { models: string[]; buildParents: boolean; runTests: boolean } | null,
  runnableIds: string[] | null,
): RunDbtJobsBody {
  if (chosen) return chosen;
  return runnableIds ? { dbtJobIds: runnableIds } : {};
}

/** Starting the run: one click is one request, a refusal stays in the dialog, success closes it. */
export function useRunStarter(options: {
  chosen: string[] | null;
  buildParents: boolean;
  runTests: boolean;
  /** The runnable dbt job ids when a selection was passed, else null (no body). */
  selectedIds: string[] | null;
  idempotencyKey: string;
  /** From the queue read: null when nothing holds or waits for the slot. */
  queuedBehind: QueuedBehind | null;
  onOpenChange: (open: boolean) => void;
}) {
  const [, navigate] = useLocation();
  const notice = useNotice();
  // Session 10 (ux-s10 §3): any other refusal is said inside this dialog too, which stays open.
  const failure = useInlineError();
  const start = useStartTransformRun();
  const [refusal, setRefusal] = useState<RunRefusal | null>(null);
  // `start.isPending` reaches the button on the NEXT render (the query client
  // batches its notifications), so a second click inside that window would
  // send a second POST. Same key, so the server would answer the same run —
  // but one click is one request, and a ref is what makes that true now.
  const inFlight = useRef(false);

  const run = async (): Promise<void> => {
    if (inFlight.current) return;
    inFlight.current = true;
    setRefusal(null);
    failure.clear();
    try {
      const body = runBodyOf(
        options.chosen
          ? {
              models: options.chosen,
              buildParents: options.buildParents,
              runTests: options.runTests,
            }
          : null,
        options.selectedIds,
      );
      const result = await start.mutateAsync({ body, idempotencyKey: options.idempotencyKey });
      options.onOpenChange(false);
      // After the close, so it is posted outside the modal's hidden window, under the page
      // title with the way to the run.
      const { title, sentence } = runNotice(result, options.chosen, options.queuedBehind);
      notice.success(sentence, {
        title,
        action: {
          label: "Open the run",
          onClick: () => navigate(absolutePath(`/runs/${result.runId}`)),
        },
      });
    } catch (error) {
      const details = transformNotReadyDetails(error);
      if (details) {
        setRefusal({
          checklist: notReadyChecklist(details),
          message: (error as Error).message,
          reference: referenceIdOf(error),
        });
        return;
      }
      failure.show(error, "The run could not be started.");
    } finally {
      inFlight.current = false;
    }
  };

  return { run, refusal, failure, isPending: start.isPending };
}

/** Minted once per open, and again when a chosen-models option changes the body (r6dbtui review r3 nit). */
export function useIdempotencyKey() {
  // A retry after a lost 202 must not replay the first run with the options the person has since
  // changed.
  const [idempotencyKey, setIdempotencyKey] = useState(newIdempotencyKey);
  const rekey = () => setIdempotencyKey(newIdempotencyKey());
  return { idempotencyKey, rekey };
}

/** How many recent transform steps the dialog reads for the queue: enough for the holder and its waiters. */
const TRANSFORM_QUEUE_LOOKBACK = 20;

/**
 * Round 6, T1: what the instance's one dbt slot holds, from the recent transform steps. A run of
 * chosen models is a duplicate only of the same selection still waiting (the server's
 * `assertNotAlreadyQueued` compares the same terms); it pins no dbt job.
 */
export function useRunQueue(options: {
  chosenSelect: string | null;
  requestIds: string[];
  runInFlight: boolean;
}) {
  const recent = useDbtJobRuns(TRANSFORM_QUEUE_LOOKBACK);
  const rows = recent.data?.rows ?? [];
  const queue =
    options.chosenSelect === null
      ? transformQueueOf(rows, options.requestIds)
      : transformQueueOf(rows, [], options.chosenSelect);
  // The parent's bare in-flight flag counts until this read has answered since the dialog opened.
  // A cached answer from an earlier open, or a failed read, does not settle it. Once it has, the
  // queue read alone decides, so the button, the bar and the banner always agree (#1419).
  const settled = recent.isSuccess && recent.isFetchedAfterMount;
  const unsettled = options.runInFlight && !settled;
  const inQueue = queue.holder !== null || queue.waiting > 0;
  const queues = inQueue || unsettled;
  const queuedBehind: QueuedBehind | null =
    inQueue || unsettled
      ? { holderRunId: queue.holder?.runId ?? null, waiting: queue.waiting }
      : null;
  return { queue, queues, queuedBehind };
}
